(() => {
  const INSTANCES = new WeakSet();
  const MOBILE_BREAKPOINT = "(max-width: 640px)";
  const NEARBY_VIDEO_RADIUS = 2;

  function hydrateVideo(video, preload = "metadata") {
    if (!video) return false;

    let updated = false;
    video.querySelectorAll("source[data-src]").forEach((source) => {
      const src = source.dataset.src;
      if (!src || source.getAttribute("src") === src) return;

      source.setAttribute("src", src);
      updated = true;
    });

    if (updated) {
      video.preload = preload;
      video.dataset.lazySourceLoaded = "true";
      video.load();
    }

    if (video.dataset.lazySourceLoaded === "true") {
      video.preload = preload;
    }

    return updated;
  }

  function releaseVideo(video) {
    if (!video || video.dataset.lazySourceLoaded !== "true") return;

    video.onended = null;
    video.pause();
    video.querySelectorAll("source[src]").forEach((source) => {
      source.removeAttribute("src");
    });
    video.preload = "none";
    video.dataset.lazySourceLoaded = "false";
    video.load();
  }

  function syncNearbyVideoSources(slides, currentIndex, options = {}) {
    const {
      isMobileCarousel = false,
      nextIndex = null,
      radius = NEARBY_VIDEO_RADIUS
    } = options;
    const mobileKeepAliveIndexes = new Set(
      [currentIndex, currentIndex + 1, nextIndex].filter((index) => Number.isFinite(index))
    );

    slides.forEach((slide, index) => {
      const video = slide.querySelector(".ugc-video");
      if (!video) return;

      if (isMobileCarousel ? mobileKeepAliveIndexes.has(index) : Math.abs(index - currentIndex) <= radius) {
        hydrateVideo(video, index === currentIndex ? "auto" : "metadata");
        return;
      }

      releaseVideo(video);
    });
  }

  function initCarousel(section) {
    if (!section || INSTANCES.has(section)) return;
    INSTANCES.add(section);

    const carousel = section.querySelector("[data-home-community-carousel]");
    const track = section.querySelector("[data-home-community-track]");
    if (!carousel || !track) return;

    const seedSlides = [...track.querySelectorAll("[data-community-item]")];
    if (!seedSlides.length) return;

    const isMobileCarousel = window.matchMedia(MOBILE_BREAKPOINT).matches;
    carousel.classList.toggle("is-mobile-loop", isMobileCarousel);
    carousel.classList.remove("is-mobile-native");

    const originals = seedSlides.map((slide) => slide.cloneNode(true));
    const slidesPerSet = originals.length;
    const loopCopies = slidesPerSet > 1 ? 3 : 1;
    const middleSet = loopCopies > 1 ? 1 : 0;

    track.innerHTML = "";
    for (let copyIndex = 0; copyIndex < loopCopies; copyIndex += 1) {
      originals.forEach((original, originalIndex) => {
        const clone = original.cloneNode(true);
        clone.dataset.loopCopy = String(copyIndex);
        clone.dataset.loopIndex = String(originalIndex);
        track.appendChild(clone);
      });
    }

    let slides = [...track.querySelectorAll("[data-community-item]")];
    let currentIndex = slidesPerSet * middleSet;
    let slideStep = 0;
    let pointerDown = false;
    let isDragging = false;
    let dragMoved = false;
    let suppressClick = false;
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let deltaY = 0;
    let dragAxis = null;
    let dragStartTranslate = 0;
    let activeTransition = false;
    const transitionMs = window.matchMedia("(max-width: 720px)").matches ? 380 : 440;
    const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
    const videoSourceRadius = NEARBY_VIDEO_RADIUS;
    const initialBounds = section.getBoundingClientRect();
    let isSectionVisible = initialBounds.bottom > 0 && initialBounds.top < window.innerHeight;

    function getSlideWidth() {
      return slides[0]?.getBoundingClientRect().width || 0;
    }

    function getGap() {
      return parseFloat(getComputedStyle(track).gap || "0") || 0;
    }

    function offsetFor(index) {
      const width = getSlideWidth();
      slideStep = width + getGap();
      return carousel.clientWidth / 2 - width / 2 - index * slideStep;
    }

    function getCurrentTranslate() {
      const transform = window.getComputedStyle(track).transform;
      if (!transform || transform === "none") return offsetFor(currentIndex);

      const matrix = new window.DOMMatrixReadOnly(transform);
      return matrix.m41;
    }

    function syncIndexToTranslate(translate) {
      if (slidesPerSet < 2) {
        currentIndex = 0;
        return currentIndex;
      }

      offsetFor(currentIndex);
      const centeredOffset = carousel.clientWidth / 2 - getSlideWidth() / 2;
      const rawIndex = (centeredOffset - translate) / slideStep;
      const nearestIndex = Math.round(rawIndex);
      const minIndex = 0;
      const maxIndex = slides.length - 1;

      currentIndex = Math.min(maxIndex, Math.max(minIndex, nearestIndex));
      currentIndex = normalizeIndex(currentIndex);
      return currentIndex;
    }

    function interruptActiveTransition() {
      if (!activeTransition) return;

      const carryTime = (() => {
        const activeVideo = slides[currentIndex]?.querySelector(".ugc-video");
        if (!activeVideo || activeVideo.paused) return null;
        return activeVideo.currentTime;
      })();
      const currentTranslate = getCurrentTranslate();

      track.style.transition = "none";
      track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      activeTransition = false;
      syncIndexToTranslate(currentTranslate);
      applyActiveState(Number.isFinite(carryTime) ? { resumeAt: carryTime, keepTime: true } : { keepTime: true });
    }

    function normalizeIndex(index) {
      if (slidesPerSet < 2) return 0;
      if (index < slidesPerSet) return index + slidesPerSet;
      if (index >= slidesPerSet * 2) return index - slidesPerSet;
      return index;
    }

    function pauseVideo(video, slide, keepTime = false) {
      video.onended = null;
      video.pause();
      if (!keepTime) {
        try {
          video.currentTime = Number(slide.dataset.startAt || 0);
        } catch (error) {}
      }
    }

    function playVideo(video, slide, options = {}) {
      if (!isSectionVisible || document.hidden) {
        pauseVideo(video, slide, true);
        return;
      }
      const startAt = Number(slide.dataset.startAt || 0);
      const resumeAt = Number.isFinite(options.resumeAt) ? options.resumeAt : startAt;
      hydrateVideo(video, "auto");
      try {
        video.currentTime = resumeAt;
      } catch (error) {}
      video.onended = () => {
        if (isDragging || activeTransition) return;
        goTo(currentIndex + 1, true);
      };
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    function applyActiveState(options = {}) {
      const { autoplay = true, keepTime = false, resumeAt = null } = options;
      syncNearbyVideoSources(slides, currentIndex, {
        isMobileCarousel,
        nextIndex: normalizeIndex(currentIndex + 1),
        radius: videoSourceRadius
      });
      slides.forEach((slide, index) => {
        const isActive = index === currentIndex;
        slide.classList.toggle("is-active", isActive);
        const video = slide.querySelector(".ugc-video");
        if (!video) return;

        if (!isActive) {
          pauseVideo(video, slide, keepTime);
          return;
        }

        if (!autoplay) return;
        playVideo(video, slide, { resumeAt });
      });
      track.dataset.currentIndex = String(currentIndex);
    }

    function setTrackPosition(animate = true, options = {}) {
      track.style.transition = animate ? `transform ${transitionMs}ms ${easing}` : "none";
      track.style.transform = `translate3d(${offsetFor(currentIndex)}px, 0, 0)`;
      if (!animate) {
        activeTransition = false;
        applyActiveState(options);
      }
    }

    function goTo(index, animate = true, options = {}) {
      if (activeTransition && animate) return;
      currentIndex = index;
      activeTransition = animate;
      applyActiveState({ autoplay: !animate, keepTime: true, ...options });
      track.style.transition = animate ? `transform ${transitionMs}ms ${easing}` : "none";
      track.style.transform = `translate3d(${offsetFor(currentIndex)}px, 0, 0)`;
      if (!animate) {
        activeTransition = false;
        applyActiveState(options);
      }
    }

    function snapFromDrag() {
      const threshold = Math.min(92, slideStep * 0.12);
      if (deltaX <= -threshold) {
        goTo(currentIndex + 1, true);
      } else if (deltaX >= threshold) {
        goTo(currentIndex - 1, true);
      } else {
        goTo(currentIndex, true);
      }
    }

    carousel.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const dragHandle = event.target.closest("[data-community-drag-handle]");
      const mediaLink = event.target.closest(".ugc-media-link");
      const canDragFromMobileMedia = isMobileCarousel && mediaLink && carousel.contains(mediaLink);
      if ((!dragHandle || !carousel.contains(dragHandle)) && !canDragFromMobileMedia) return;
      pointerDown = true;
      isDragging = false;
      dragMoved = false;
      dragAxis = null;
      deltaX = 0;
      deltaY = 0;
      startX = event.clientX;
      startY = event.clientY;
      dragStartTranslate = getCurrentTranslate();
    });

    carousel.addEventListener("pointermove", (event) => {
      if (!pointerDown) return;
      deltaX = event.clientX - startX;
      deltaY = event.clientY - startY;
      const dragStartThreshold = isMobileCarousel ? 8 : 8;
      const horizontalIntentRatio = isMobileCarousel ? 0.72 : 1.05;
      const verticalIntentRatio = isMobileCarousel ? 1.55 : 1.02;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!dragAxis && (absX > dragStartThreshold || absY > dragStartThreshold)) {
        if (absX > absY * horizontalIntentRatio) {
          dragAxis = "x";
          interruptActiveTransition();
          dragStartTranslate = getCurrentTranslate();
          try {
            carousel.setPointerCapture(event.pointerId);
          } catch (error) {}
        } else if (absY > absX * verticalIntentRatio) {
          dragAxis = "y";
          pointerDown = false;
          isDragging = false;
          dragMoved = false;
          carousel.classList.remove("is-dragging");
          try {
            if (carousel.hasPointerCapture?.(event.pointerId)) {
              carousel.releasePointerCapture(event.pointerId);
            }
          } catch (error) {}
          return;
        } else {
          return;
        }
      }

      if (dragAxis !== "x") return;

      if (!isDragging) {
        isDragging = true;
        dragMoved = true;
        carousel.classList.add("is-dragging");
        track.style.transition = "none";
      }

      if (!isDragging) return;

      track.style.transform = `translate3d(${dragStartTranslate + deltaX}px, 0, 0)`;
      event.preventDefault();
    });

    function finishDrag(event) {
      if (!pointerDown) return;
      pointerDown = false;
      carousel.classList.remove("is-dragging");
      try {
        if (carousel.hasPointerCapture?.(event.pointerId)) {
          carousel.releasePointerCapture(event.pointerId);
        }
      } catch (error) {}

      if (!isDragging) return;
      isDragging = false;
      dragAxis = null;

      if (dragMoved) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 90);
      }
      snapFromDrag();
    }

    carousel.addEventListener("pointerup", finishDrag);
    carousel.addEventListener("pointercancel", finishDrag);
    carousel.addEventListener("pointerleave", (event) => {
      if (pointerDown && event.pointerType === "mouse") finishDrag(event);
    });

    carousel.addEventListener(
      "click",
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    track.addEventListener("transitionend", (event) => {
      if (event.target !== track || event.propertyName !== "transform") return;

      let carryTime = null;
      const activeVideo = slides[currentIndex]?.querySelector(".ugc-video");
      if (activeVideo && !activeVideo.paused) {
        carryTime = activeVideo.currentTime;
      }

      currentIndex = normalizeIndex(currentIndex);
      track.style.transition = "none";
      track.style.transform = `translate3d(${offsetFor(currentIndex)}px, 0, 0)`;
      activeTransition = false;
      applyActiveState(Number.isFinite(carryTime) ? { resumeAt: carryTime, keepTime: true } : {});
    });

    slides.forEach((slide) => {
      const video = slide.querySelector(".ugc-video");
      if (!video) return;
      slide.dataset.startAt = slide.dataset.startAt || "0";
      video.addEventListener(
        "loadedmetadata",
        () => {
          if (!slide.classList.contains("is-active")) {
            try {
              video.currentTime = Number(slide.dataset.startAt || 0);
            } catch (error) {}
          }
        },
        { once: true }
      );
    });

    window.addEventListener("resize", () => {
      slides = [...track.querySelectorAll("[data-community-item]")];
      currentIndex = normalizeIndex(currentIndex);
      setTrackPosition(false, { keepTime: true });
    });

    function syncVideoVisibility() {
      if (!isSectionVisible || document.hidden) {
        slides.forEach((slide) => {
          const video = slide.querySelector(".ugc-video");
          if (video) pauseVideo(video, slide, true);
        });
        return;
      }
      if (isDragging || activeTransition) return;
      const slide = slides[currentIndex];
      const video = slide?.querySelector(".ugc-video");
      if (video && video.paused) {
        playVideo(video, slide, {
          resumeAt: Math.max(video.currentTime, Number(slide.dataset.startAt || 0))
        });
      }
    }

    if ("IntersectionObserver" in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        isSectionVisible = entries.some((entry) => entry.isIntersecting);
        syncVideoVisibility();
      });
      visibilityObserver.observe(section);
    }
    document.addEventListener("visibilitychange", syncVideoVisibility);
    setTrackPosition(false);
  }

  function initWhenNearViewport(section) {
    if (!section || section.dataset.homeCommunityLazyBound === "true") return;
    section.dataset.homeCommunityLazyBound = "true";

    if (!("IntersectionObserver" in window)) {
      initCarousel(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initCarousel(section);
      },
      { rootMargin: "520px 0px" }
    );
    observer.observe(section);
  }

  function initAll() {
    document.querySelectorAll("[data-home-community-loop]").forEach(initWhenNearViewport);
  }

  document.addEventListener("DOMContentLoaded", initAll);
})();
